import React, {
  Dispatch,
  SetStateAction,
  useState,
  useEffect,
  useRef,
  useContext,
} from 'react';
import { IPriceInfo, IPromoCode } from 'pages/newSubscription/models';
import FormGeneralError from 'components/formComponents/FormGeneralError';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectSelectedPackages,
  selectValidPromoCode,
  setValidPromoCode,
} from 'reducers/misc';
import { PricingSummaryDetailsApi } from './pricingSummaryDetailsApi';
import { Spinner } from 'components/spinner/Spinner';
import { SubscriptionDoubleSubDiscountModal } from '../../pages/subscriptions/SubscriptionDoubleSubDiscountModal';
import { SubscriptionDoubleSubDiscountOverrideModal } from '../../pages/subscriptions/SubscriptionDoubleSubDiscountOverrideModal';
import { SubscriptionDoubleSubDiscountSameLengthSubs } from '../../pages/subscriptions/SubscriptionDoubleSubDiscountSameLengthSubs';
import { SubscriptionDoubleSubDiscountAlignPackages } from '../../pages/subscriptions/SubscriptionDoubleSubDiscountAlignPackages';
import { FormikContext, FormikContextType } from 'formik';
import { selectQuotesId } from 'reducers/misc';
import { selectSignedIn } from 'reducers/auth';
import {
  buildBasketOrders,
  buildSelectionOrders,
  pickSelectionOrders,
} from 'subscriptionOptions/pricingRequest';
import { selectionToPackageIds } from 'subscriptionOptions/optionsGrid';
import { AccountContext, Basket, PackagePromo, PackageResult } from 'subscriptionOptions/types';
import { REVERSE_CHARGE_NOTE, vatDisplay } from 'subscriptionOptions/vatDisplay';
import {
  displayRows,
  HeldPromoCode,
} from 'subscriptionOptions/renewalPromoCodes';
import {
  codesForPricing,
  PromoCodeSlots,
} from 'subscriptionOptions/promoCodeSlots';
import { createPriceSequence } from 'subscriptionOptions/priceSequence';
import { AppliedPromoCodes } from 'components/appliedPromoCodes/AppliedPromoCodes';

type PricingSummaryDetailsProps = {
  addPromoStep: boolean;
  promoCode?: IPromoCode;
  setPromoCode?: Dispatch<SetStateAction<IPromoCode>>;
  anyFreeTrialSub?: boolean;
  anyFullPaidSub?: boolean;
  existingMembershipOptInfo?: any;
  /** Signed-in pages only; decides how the VAT row reads. */
  accountContext?: AccountContext | null;
  applyPromoCode?: () => Promise<void>;
  setPriceInfo?: Dispatch<SetStateAction<IPriceInfo>>;
  onContinue?: () => void;
  onSecondBtnContinue?: () => void;
  preContinueFunc?: () => void;
  isDisabled?: any;
  isSubmitting?: any;
  btnText?: string;
  secondBtnTxt?: string;
  initApply?: boolean;
  renewalSummary?: boolean;
  /** Called after pricing accepts a code from the promo box. */
  onPromoCodeApplied?: (promoCode: string) => void;
  /** The pairs this panel priced, so payment sends what produced the amount shown. */
  onPricedPromos?: (packagePromos: PackagePromo[]) => void;
  /**
   * Basket from the parent rather than Redux, so several summaries can be mounted at once without
   * pricing each other's packages. Each subject carries its own code.
   */
  basket?: Basket;
  /** False while a subject is unpicked: nothing coherent to price yet. */
  basketReady?: boolean;
  /** True while the owner is saving. Pricing waits, because the save reports which codes it wiped. */
  basketPending?: boolean;
  /** The codes held for this basket, listed under the promo input. */
  appliedPromoCodes?: HeldPromoCode[];
  /** A quote-backed basket's codes, one per subject; converted for the pricing call only. */
  promoCodeSlots?: PromoCodeSlots;
  /** Every package the quote holds, so a slot resolves to the packages being priced. */
  quotePackages?: any[];
  /** Set when the codes could not be read; pricing is blocked rather than quoted from stale ones. */
  promoCodesError?: string;
  /** A rejected code. Never blocks pricing. */
  promoCodeRejection?: string;
  /** Codes an apply displaced. */
  droppedPromoCodes?: string[];
  /** Explains a wiped code, naming the subjects that lost it. */
  wipedNotice?: string;
  /** Codes a quote named that have ended since it was made. */
  expiredPromoCodes?: string[];
  onRemovePromoCode?: (promoCode: string) => void;
  /** Keyed by subjectsID, rendered under that subject's prices. Renewal panels only. */
  subjectNotices?: { [subjectsId: number]: string };
  /** Nothing in the basket can be paid yet: totals are hidden and the buttons disabled. */
  paymentPending?: boolean;
  /** Every package as last priced, pending ones included; empty while there is no price. */
  onPackagesPriced?: (packages: PackageResult[]) => void;
};

/** Identifies what was last priced, so an unchanged basket does not trigger another call. */
const basketKey = (basket: Basket) =>
  JSON.stringify([
    basket.selection,
    basket.promoCodeBySubject,
    basket.pendingSubjectIds || [],
  ]);

/** Each package with the order that priced it, which its discount type is read from. */
const summaryRows = (order: any): { order: any; pkg: any }[] => {
  const rows = (order.packageResults || []).map((pkg: any) => ({ order, pkg }));
  const pending = order.pendingOrderResult;

  if (!pending) {
    return rows;
  }

  const pendingRows = (pending.packageResults || []).map((pkg: any) => ({
    order: pending,
    pkg,
  }));

  return rows
    .concat(pendingRows)
    .sort((a: any, b: any) => a.pkg.subjectsID - b.pkg.subjectsID);
};

export const PricingSummaryDetails: React.FC<PricingSummaryDetailsProps> = ({
  addPromoStep,
  promoCode,
  setPromoCode,
  setPriceInfo,
  anyFreeTrialSub,
  anyFullPaidSub,
  existingMembershipOptInfo,
  accountContext,
  applyPromoCode,
  onContinue,
  onSecondBtnContinue,
  preContinueFunc,
  isDisabled,
  isSubmitting,
  btnText,
  secondBtnTxt,
  initApply,
  renewalSummary,
  onPromoCodeApplied,
  onPricedPromos,
  basket,
  basketReady,
  basketPending,
  appliedPromoCodes,
  promoCodeSlots,
  quotePackages,
  promoCodesError,
  promoCodeRejection,
  droppedPromoCodes,
  wipedNotice,
  expiredPromoCodes,
  onRemovePromoCode,
  subjectNotices,
  paymentPending,
  onPackagesPriced,
}) => {
  const [reduxPackageListIds, setPackageListIds] = useState<number[]>([]);
  const lastPricedBasket = useRef<string>('');
  const [psData, setPsData] = useState<any>(null);
  const [psDataUpdated, setPsDataUpdated] = useState<any>(null);
  const [psDataLoading, setPsDataLoading] = useState<boolean>(true);
  const [psDataError, setPsDataError] = useState<string>('');
  const [initApplyTrigger, setInitApplyTrigger] = useState<boolean>(true);
  const [dbSubDiscountMisalignPopup, setDbSubDiscountMisalignPopup] =
    useState<boolean>(false);
  const [hasMultiPurchaseDiscountApplied, setHasMultiPurchaseDiscountApplied] =
    useState<boolean>(false);
  const [dbSubOverrideDiscountPopup, setDbSubOverrideDiscountPopup] =
    useState<boolean>(false);
  const [
    hasMultiPurchaseOverrideDiscount,
    setHasMultiPurchaseOverrideDiscount,
  ] = useState<boolean>(false);
  const [
    hasMultiPurchaseLengthSubsMisaligned,
    setHasMultiPurchaseLengthSubsMisaligned,
  ] = useState<boolean>(false);
  const [dbSubSameLengthSubsPopup, setDbSubSameLengthSubsPopup] =
    useState<boolean>(false);
  const [
    hasMultiPurchasePackagesMisaligned,
    setHasMultiPurchasePackagesMisaligned,
  ] = useState<boolean>(false);
  const [dbSubAlignPkgsPopup, setDbSubAlignPkgsPopup] =
    useState<boolean>(false);
  const [collectivePkgPromoCodes, setCollectivePkgPromoCodes] =
    useState<string>('');
  const [individualPkgPromoCodes, setIndividualPkgPromoCodes] = useState<
    string[]
  >([]);

  const isSignedIn = useSelector(selectSignedIn);
  const selectedPackagesList = useSelector(selectSelectedPackages);
  const validPromoCode = useSelector(selectValidPromoCode);
  const quotesId: number | null = useSelector(selectQuotesId);

  // Read the context directly rather than via useFormikContext: the promo step renders
  // outside a <Formik> provider, and the hook warns when the context is undefined.
  const formik = useContext<FormikContextType<any> | undefined>(FormikContext);
  const formikContext = addPromoStep ? null : formik;

  const dispatch = useDispatch();

  const basketSerialised = basket ? basketKey(basket) : null;
  /** Identifies the quote's codes, so applying or removing one reprices. */
  const promoCodeSlotsSerialised = promoCodeSlots
    ? JSON.stringify(promoCodeSlots)
    : null;
  /** Derived, so a basket change cannot leave the ids a render behind the reprice guard's key. */
  const packageListIds = basket
    ? selectionToPackageIds(basket.selection)
    : reduxPackageListIds;
  const hasBasket = !!basket;
  /** Nothing to price until every subject has a package and the codes are trustworthy. */
  const basketPriceable =
    !basket || (basketReady !== false && !basketPending && !promoCodesError);
  /** Decides which pricing response is still wanted, so a slow one cannot overwrite a newer one. */
  const priceSequence = useRef(createPriceSequence());

  useEffect(() => {
    if (basket) {
      return;
    }

    let packageIdsList: number[] = [];

    selectedPackagesList.map((item: any) => {
      item.packageIds.map((pi_item: number) => {
        packageIdsList.push(pi_item);

        return pi_item;
      });

      return item;
    });

    setPackageListIds(packageIdsList);
    // `basket` is a new object each render, so depend on its presence, not the object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPackagesList, hasBasket]);

  useEffect(() => {
    if (initApply && initApplyTrigger && applyPromoCode) {
      applyPromoCode();
      setInitApplyTrigger(false);
    }
  }, [initApply]);

  useEffect(() => {
    if (basket) {
      if (!basketPriceable) {
        // Discards work in flight, so the spinner must stop with it: its response will be
        // rejected as stale and cannot clear the panel itself.
        priceSequence.current.invalidate();
        setPsDataError(promoCodesError || '');
        setPsDataLoading(!!basketPending);

        // An unreadable or incomplete basket has no price; a pending save still shows its last one.
        if (!basketPending) {
          lastPricedBasket.current = '';
          setPsData(null);
        }
        return;
      }

      const key = basketKey(basket);
      if (key === lastPricedBasket.current) {
        return;
      }
      lastPricedBasket.current = key;

      if (packageListIds.length === 0) {
        setPsData(null);
        setPsDataError('');
        setPsDataLoading(false);
        return;
      }

      loadPSData();
      return;
    }

    if (packageListIds && packageListIds.length > 0) {
      loadPSData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    packageListIds,
    anyFreeTrialSub,
    anyFullPaidSub,
    basketSerialised,
    promoCodeSlotsSerialised,
    basketPriceable,
    basketPending,
    promoCodesError,
  ]);

  useEffect(() => {
    if (psData && setPriceInfo) {
      setPriceInfo({
        price: psData.totalNetUndiscounted,
        vat: psData.totalVatOnDiscounted,
        total: psData.totalGrossDiscounted,
        discount: psData.totalNetSaving,
        appliedDiscountType: '',
      });
    }
  }, [psData]);

  const pricedRows = psData ? summaryRows(psData) : [];

  useEffect(() => {
    if (onPackagesPriced) {
      onPackagesPriced(pricedRows.map((row) => row.pkg));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [psData]);

  useEffect(() => {
    if (psData && psDataUpdated) {
      setPsData(psData);
      setPsDataUpdated(false);
    }
  }, [psData, psDataUpdated]);

  const loadPSData = async () => {
    // Claimed here, before either loader runs, so one request is superseded by exactly one
    // other. Claiming inside the loaders let two racing calls each supersede the other.
    const sequence = priceSequence.current.claim();
    setPsDataLoading(true);
    setPsDataUpdated(true);
    setPsDataError('');

    const promoCodesList = packageListIds.map(
      (_: any, idx: number) =>
        individualPkgPromoCodes[idx] || validPromoCode[idx] || ''
    );

    if (isSignedIn) {
      loadSchoolPSData(promoCodesList, sequence);
    }

    if (!isSignedIn) {
      loadPublicPSData(promoCodesList, sequence);
    }
  };

  const loadSchoolPSData = async (
    promoCodesList: string[] = individualPkgPromoCodes,
    sequence: number = priceSequence.current.claim()
  ): Promise<boolean> => {
    // Gated on the packages, not the slots: an empty slots object is truthy and would price
    // every code away.
    const pricedPromos =
      quotePackages && quotePackages.length > 0 && promoCodeSlots
        ? codesForPricing(packageListIds, promoCodeSlots, quotePackages)
        : null;
    const slotCodes = pricedPromos
      ? pricedPromos.map((p) => p.promoCode)
      : promoCodesList;

    const orders = basket
      ? buildSelectionOrders(
          basket.selection,
          basket.promoCodeBySubject,
          basket.pendingSubjectIds
        )
      : buildBasketOrders(packageListIds, slotCodes);

    if (onPricedPromos) {
      onPricedPromos(orders.orders[0].packagePromos);
    }

    let existingAccountResult =
      await PricingSummaryDetailsApi.getSchoolPricingSummaryDetails(orders);

    return handlePSDataUpdate(
      existingAccountResult,
      sequence,
      orders.orders.length > 1
    );
  };

  const loadPublicPSData = async (
    promoCodesList: string[] = individualPkgPromoCodes,
    sequence: number = priceSequence.current.claim()
  ): Promise<boolean> => {
    // Codes are kept per subject, so the positional list cannot carry them. Gated on the
    // packages, not the slots: an empty slots object is truthy and would price every code away.
    let packagePromosList =
      quotePackages && quotePackages.length > 0 && promoCodeSlots
        ? codesForPricing(packageListIds, promoCodeSlots, quotePackages)
        : packageListIds.map((id, idx) => {
            return {
              packagesId: id,
              promoCode: promoCodesList[idx] || '',
            };
          });

    if (onPricedPromos) {
      onPricedPromos(packagePromosList);
    }

    let nonExistingAccountResult =
      await PricingSummaryDetailsApi.getPublicPricingSummaryDetails({
        quotesId: quotesId,
        packagePromos: packagePromosList,
      });

    return handlePSDataUpdate(nonExistingAccountResult, sequence);
  };

  /** True when pricing loaded. A superseded response is dropped, so it cannot leave a stale price. */
  const handlePSDataUpdate = (
    pricing_result: any,
    sequence: number,
    split: boolean = false
  ): boolean => {
    if (!priceSequence.current.isCurrent(sequence)) {
      // Superseded. Only stop the spinner if nothing newer is still loading, or this response
      // would report the panel idle while its replacement is still in flight.
      if (!priceSequence.current.hasOutstanding()) {
        setPsDataLoading(false);
      }
      return false;
    }

    priceSequence.current.settle(sequence);

    setPsDataError('');

    if (!pricing_result.error) {
      const picked = pickSelectionOrders(pricing_result.data, split);

      if ('order' in picked) {
        const pricedOrder = picked.order;
        let orderResults: any = pricedOrder.orderDiscountResult;

        orderResults.packageResults = orderResults.packageResults.map(
          (pkg: any) => {
            pkg.showPromoCodeField = false;
            return pkg;
          }
        );

        orderResults.bGeneralPackagesIdMatch =
          pricedOrder.bGeneralPackagesIdMatch;
        orderResults.bSubscriptionTypesIdMatch =
          pricedOrder.bSubscriptionTypesIdMatch;
        orderResults.pendingOrderResult = picked.pendingOrder
          ? picked.pendingOrder.orderDiscountResult
          : null;

        const packageResultsPromoCodes = (
          orderResults.packageResults || []
        ).map((pkg: any) => pkg.promoCode || '');
        const hydratedPromoCodes = (orderResults.packageResults || []).map(
          (_: any, idx: number) => {
            if (basket) {
              return packageResultsPromoCodes[idx] || '';
            }
            return validPromoCode[idx] || packageResultsPromoCodes[idx] || '';
          }
        );

        setPsData(orderResults);
        setPsDataUpdated(true);
        setPsDataLoading(false);
        setIndividualPkgPromoCodes(hydratedPromoCodes);

        // A prop-driven basket owns its code; publishing to the shared store would leak it across panels.
        const hasValidPromoCodeChanged =
          !basket &&
          (validPromoCode.length !== packageResultsPromoCodes.length ||
            validPromoCode.some(
              (code: string, idx: number) =>
                code !== packageResultsPromoCodes[idx]
            ));

        if (hasValidPromoCodeChanged) {
          dispatch(setValidPromoCode(packageResultsPromoCodes));
        }

        if (anyFullPaidSub && existingMembershipOptInfo === null) {
          setHasMultiPurchaseDiscountApplied(
            orderResults.MultiPurchaseDiscountApplied
          );
          setHasMultiPurchaseOverrideDiscount(
            orderResults.promoCodeOverriddenBySystemDiscount ||
              orderResults.systemDiscountOverriddenByPromoCode
          );
        } else {
          if (
            anyFreeTrialSub &&
            !orderResults.bSingleSubject &&
            (!orderResults.bGeneralPackagesIdMatch ||
              !orderResults.bSubscriptionTypesIdMatch) &&
            existingMembershipOptInfo === null
          ) {
            if (!orderResults.bGeneralPackagesIdMatch) {
              setHasMultiPurchasePackagesMisaligned(true);
            } else if (!orderResults.bSubscriptionTypesIdMatch) {
              setHasMultiPurchaseLengthSubsMisaligned(true);
            }
          }
        }

        return true;
      } else {
        // The panel draws one basket; several orders means the request was wrong.
        console.warn(picked.error);
        setPsData(null);
        setPsDataError(picked.error);
        setPsDataLoading(false);
      }
    } else {
      if (pricing_result.error && pricing_result.error.message) {
        setPsDataError(pricing_result.error.message);
      }

      setPsData(null);

      setPsDataLoading(false);
    }

    return false;
  };

  /** One row per distinct code. A code that won nothing keeps its row, so it stays removable. */
  const promoCodeRows = displayRows(
    appliedPromoCodes || [],
    pricedRows.map((row) => row.pkg)
  );

  const applyCollectivePromoCode = async () => {
    const code = collectivePkgPromoCodes;
    if (!code) return;

    setCollectivePkgPromoCodes('');

    /**
     * A renewal basket's codes belong to the account. The write decides which packages a code lands
     * on and its response drives the reprice, so pricing here first would quote an unaccepted code.
     */
    if (basket) {
      if (onPromoCodeApplied) {
        onPromoCodeApplied(code);
      }
      return;
    }

    /**
     * checkpromocode decides which slots a code fills, so pricing first would quote a code it
     * may refuse. Only where the quote's packages are known; otherwise the path below applies it.
     */
    if (quotePackages && quotePackages.length > 0 && onPromoCodeApplied) {
      onPromoCodeApplied(code);
      return;
    }

    if (packageListIds.length === 0) return;

    const promoCodesList = packageListIds.map(() => code);
    setIndividualPkgPromoCodes(promoCodesList);

    const accepted = isSignedIn
      ? await loadSchoolPSData(promoCodesList)
      : await loadPublicPSData(promoCodesList);

    if (accepted && code && onPromoCodeApplied) {
      onPromoCodeApplied(code);
    }
  };

  const checkDoubleDiscount = () => {
    const submitOrContinue = () => {
      if (addPromoStep && onContinue) {
        onContinue();
      } else if (formikContext) {
        formikContext.submitForm();
      }
    };

    if (hasMultiPurchaseDiscountApplied) {
      setDbSubDiscountMisalignPopup(true);
    } else if (hasMultiPurchaseOverrideDiscount) {
      setDbSubOverrideDiscountPopup(true);
    } else if (hasMultiPurchaseLengthSubsMisaligned) {
      setDbSubSameLengthSubsPopup(true);
    } else if (hasMultiPurchasePackagesMisaligned) {
      const hasMultiplePackagesSelected = packageListIds.length > 1;
      const hasSinglePackageSelectedAndAnotherAlreadySubscribed =
        psData?.bSingleSubject && packageListIds.length === 1 && anyFullPaidSub;

      if (
        isSignedIn &&
        (hasMultiplePackagesSelected ||
          hasSinglePackageSelectedAndAnotherAlreadySubscribed)
      ) {
        setDbSubAlignPkgsPopup(true);
      } else {
        submitOrContinue();
      }
    } else {
      submitOrContinue();
    }
  };

  const submitWithPreFunc = () => {
    if (preContinueFunc) {
      preContinueFunc();
    }
  };

  const switchPopupMsgs = () => {
    setDbSubDiscountMisalignPopup(false);

    setTimeout(function () {
      setDbSubOverrideDiscountPopup(true);
    }, 300);
  };

  const discountTypeRow = (psData: any, pkg: any) => {
    let discountType = '';
    let dtAssigned = false;

    //check for discount types within package
    if (!dtAssigned && pkg.multiPurchaseDiscountApplied) {
      discountType = 'Double subject';
      dtAssigned = true;
    }
    if (!dtAssigned && pkg.unbrokenServiceDiscountApplied) {
      discountType = 'Service';
      dtAssigned = true;
    }
    if (!dtAssigned && pkg.promoCodeDiscountApplied) {
      discountType = 'Promo code';
      dtAssigned = true;
    }

    //if no package discount types, check for discount types within order
    if (!dtAssigned && psData.multiPurchaseDiscountApplied) {
      discountType = 'Double subject';
      dtAssigned = true;
    }
    if (!dtAssigned && psData.unbrokenServiceDiscountApplied) {
      discountType = 'Service';
      dtAssigned = true;
    }
    if (!dtAssigned && psData.promoCodeDiscountApplied) {
      discountType = 'Promo code';
      dtAssigned = true;
    }

    if (discountType) {
      return (
        <div className="SDBRow">
          <div className="SDBLeftCol">Discount type</div>
          <div className="SDBRightCol">
            {pkg.promoCodeDiscountApplied ? (
              <>
                Promo code
                <br />
                <b>{pkg.promoCode}</b>
              </>
            ) : (
              <>{discountType}</>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  const vatMode = vatDisplay(accountContext, existingMembershipOptInfo);

  return (
    <>
      <div
        className={
          'PricingSummaryBlock' +
          (psDataLoading || isSubmitting ? ' LoadingPanel' : '') +
          (renewalSummary ? ' RenewalSummary' : '')
        }
      >
        <Spinner color={'#f59e16'} />

        <div className="PSBlockInner">
          <h2>{renewalSummary ? 'Renewal ' : ''}Summary</h2>
          {psDataError && (
            <div className="ErrorTxt">
              {psDataError}
            </div>
          )}

          {addPromoStep && (
            <div className={'TotalDetails'}>
              <div className="DiscoundContainer">
                <input
                  className="Discount"
                  type="text"
                  maxLength={50}
                  onChange={(e) => {
                    setCollectivePkgPromoCodes(e.target.value);
                  }}
                  value={collectivePkgPromoCodes}
                  placeholder="Enter promo code (optional)"
                />

                <button
                  className="Btn BtnOrange"
                  type="button"
                  disabled={collectivePkgPromoCodes ? false : true}
                  onClick={applyCollectivePromoCode}
                >
                  Apply
                </button>

                {promoCode?.error && (
                  <FormGeneralError message={promoCode?.error} />
                )}
              </div>

              {promoCodeRejection && (
                <FormGeneralError message={promoCodeRejection} />
              )}

              <AppliedPromoCodes
                rows={promoCodeRows}
                onRemove={onRemovePromoCode}
                dropped={droppedPromoCodes}
                wipedNotice={wipedNotice}
                expired={expiredPromoCodes}
              />
            </div>
          )}

          {basket &&
            basketReady === false &&
            !basketPending &&
            !promoCodesError && (
              <div className="SummaryNoticeTxt">
                Please choose a package for each subject to see your renewal
                price.
              </div>
            )}

          {psData &&
            psData.packageResults &&
            psData.packageResults.length > 0 && (
              <div className="SummarySubDetails">
                {pricedRows.map(({ order, pkg }, index: number) => {
                  return (
                    <div key={index} className="SDBlock">
                      <div className="SDBRow TopRow">
                        <div className="SDBLeftCol">{pkg.subjectsName}</div>
                        <div className="SDBRightCol">
                          {pkg.generalPackagesName} -{' '}
                          <span className="SDBLength">
                            {pkg.subscriptionTypesName}
                          </span>
                        </div>
                      </div>
                      {pkg.netUndiscounted > 0 && (
                        <div className="SDBRow">
                          <div className="SDBLeftCol">Default price</div>
                          <div className="SDBRightCol">
                            £{pkg.netUndiscounted.toFixed(2)}
                          </div>
                        </div>
                      )}
                      {discountTypeRow(order, pkg)}
                      {pkg.netSaving > 0 && (
                        <div className="SDBRow">
                          <div className="SDBLeftCol">Discount amount</div>
                          <div className="SDBRightCol">
                            £{pkg.netSaving.toFixed(2)}
                          </div>
                        </div>
                      )}
                      {pkg.netSaving > 0 && pkg.netDiscounted > 0 && (
                        <div className="SDBRow BoldRow">
                          <div className="SDBLeftCol">Discounted price</div>
                          <div className="SDBRightCol">
                            £{pkg.netDiscounted.toFixed(2)}
                          </div>
                        </div>
                      )}
                      {subjectNotices && subjectNotices[pkg.subjectsID] && (
                        <p className="SummaryNoticeTxt">
                          {subjectNotices[pkg.subjectsID]}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

          <div className="TotalDetails">
            {paymentPending ? null : psData ? (
              <div className="DiscoundContainer">
                {psData.totalNetUndiscounted > -1 && (
                  <div className="DetailsItem">
                    <p>Net price</p>
                    <span>£ {psData.totalNetUndiscounted.toFixed(2)}</span>
                  </div>
                )}
                {psData.totalNetSaving > 0 && (
                  <div className="DetailsItem">
                    <p>
                      Discount
                      {/* {
                        psData.multiPurchaseDiscountApplied ?
                          " (Double subject)" :
                          psData.unbrokenServiceDiscountApplied ?
                            " (Service)" :
                            psData.promoCodeDiscountApplied ?
                              " (Promo code)" :
                              ""
                      } */}
                    </p>
                    <span>-£ {psData.totalNetSaving.toFixed(2)}</span>
                  </div>
                )}
                {psData.totalNetSaving > 0 && psData.totalNetDiscounted > 0 && (
                  <div className="DetailsItem">
                    <p>Discounted Price</p>
                    <span>£ {psData.totalNetDiscounted.toFixed(2)}</span>
                  </div>
                )}
                <div className="DetailsItem">
                  <p>
                    VAT
                    {vatMode === 'reverseCharge'
                      ? '*'
                      : vatMode === 'notApplicable'
                        ? ''
                        : '(' + psData.vatRate + '%)'}
                  </p>
                  <span>
                    {vatMode === 'reverseCharge'
                      ? '£ 0.00'
                      : vatMode === 'notApplicable'
                        ? 'Not applicable'
                        : '£' + psData.totalVatOnDiscounted.toFixed(2)}
                  </span>
                </div>
                {psData.totalGrossDiscounted > -1 && (
                  <div className="DetailsItem">
                    <p>Gross price</p>
                    <span>£ {psData.totalGrossDiscounted.toFixed(2)}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="DiscoundContainer">
                <div className="DetailsItem">
                  <p>Net price</p>
                  <span>£ 0.00</span>
                </div>
                <div className="DetailsItem">
                  <p>VAT</p>
                  <span>£ 0.00</span>
                </div>
                <div className="DetailsItem">
                  <p>Gross price</p>
                  <span>£ 0.00</span>
                </div>
              </div>
            )}

            <div className="SummaryPanelBtmBtns">
              <div className="SPBtnCol">
                {addPromoStep ? (
                  <button
                    className="Btn BtnOrange"
                    type="submit"
                    onClick={checkDoubleDiscount}
                    disabled={
                      (packageListIds && packageListIds.length === 0) ||
                      psData?.totalNetUndiscounted === 0 ||
                      psDataError !== '' ||
                      !!paymentPending ||
                      (!!basket && (!basketPriceable || !psData))
                    }
                  >
                    {btnText || 'Continue'}
                  </button>
                ) : preContinueFunc ? (
                  <button
                    className="Btn BtnOrange"
                    type="submit"
                    onClick={submitWithPreFunc}
                    disabled={isDisabled || psDataError || !!paymentPending}
                  >
                    {btnText}
                  </button>
                ) : (
                  <button
                    className="Btn BtnOrange"
                    type="submit"
                    disabled={isDisabled || psDataError || !!paymentPending}
                  >
                    {btnText}
                  </button>
                )}
              </div>
              {onSecondBtnContinue && secondBtnTxt && (
                <div className="SPBtnCol">
                  <button
                    className="Btn BtnOrange SecondSummBtn"
                    type="submit"
                    onClick={onSecondBtnContinue}
                    disabled={isDisabled || psDataError || !!paymentPending}
                  >
                    {secondBtnTxt}
                  </button>
                </div>
              )}
            </div>

            {vatMode === 'reverseCharge' && (
              <p className="VATInfoTxt">{REVERSE_CHARGE_NOTE}</p>
            )}

            {renewalSummary && !onSecondBtnContinue && (
              <p className="VATInfoTxt">
                Alternatively, an invoice will be sent to you on your renewal
                date if you choose not to pay by card.
              </p>
            )}
          </div>
        </div>
      </div>

      {dbSubDiscountMisalignPopup && (
        <SubscriptionDoubleSubDiscountModal
          panelOpen={dbSubDiscountMisalignPopup}
          togglePanelOpen={setDbSubDiscountMisalignPopup}
          onContinue={() => {
            if (hasMultiPurchaseOverrideDiscount) {
              switchPopupMsgs();
            } else if (onContinue) {
              onContinue();
            }
          }}
        />
      )}
      {dbSubOverrideDiscountPopup && (
        <SubscriptionDoubleSubDiscountOverrideModal
          panelOpen={dbSubOverrideDiscountPopup}
          togglePanelOpen={setDbSubOverrideDiscountPopup}
          onContinue={onContinue}
        />
      )}
      {dbSubSameLengthSubsPopup && (
        <SubscriptionDoubleSubDiscountSameLengthSubs
          panelOpen={dbSubSameLengthSubsPopup}
          togglePanelOpen={setDbSubSameLengthSubsPopup}
          onContinue={onContinue}
        />
      )}
      {dbSubAlignPkgsPopup && (
        <SubscriptionDoubleSubDiscountAlignPackages
          panelOpen={dbSubAlignPkgsPopup}
          togglePanelOpen={setDbSubAlignPkgsPopup}
          onContinue={onContinue}
        />
      )}
    </>
  );
};
